import { ErrorInfo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { incidentNotify } from '@/api/client';
import { TypingIndicator } from '@/components/atoms/TypingIndicator/TypingIndicator';
import { useRtmTyping } from '@/hooks/rtm/useRtmTyping';
import { cn } from '@/lib/utils';
import { formatBrainActivity } from '@/modules/humanChat/hooks/formatBrainActivity';
import { useRtmSessionStream } from '@/modules/humanChat/hooks/useRtmSessionStream';

type Props = { sessionId: string | undefined };

const TypingDots = ({ isVisible }: { isVisible: boolean }) => (
  <div
    className={cn(
      'invisible pt-4 opacity-0 transition-[opacity,visibility] duration-200',
      isVisible && 'visible opacity-100'
    )}
  >
    <TypingIndicator />
  </div>
);

const Stream = ({ sessionId, isTyping }: Props & { isTyping: boolean }) => {
  const { t } = useTranslation();
  const { draft, activity } = useRtmSessionStream(sessionId);

  return (
    <>
      {activity && (
        <p className="mr-8 pt-4 text-right text-xs leading-4 text-foreground-muted">
          {formatBrainActivity(activity, t)}
        </p>
      )}
      {draft && (
        <div className="mt-4 mr-8 flex justify-end">
          <div className="w-fit max-w-lg rounded-lg bg-background px-4 py-1.75 text-sm leading-5 font-normal break-words whitespace-pre-wrap text-foreground-muted shadow-sm">
            {draft}
          </div>
        </div>
      )}
      <TypingDots isVisible={isTyping && !activity && !draft} />
    </>
  );
};

const reportStreamError = (error: Error, info: ErrorInfo) => {
  incidentNotify({
    summary: `[BrainStream] ${error.message}`,
    component_stack: info.componentStack ?? '',
    url: window.location.pathname,
    query: window.location.search,
    host: window.location.host,
    origin: window.location.origin,
    error_name: error.name,
  });
};

/** The streamed draft and activity of a brain turn. A failure falls back to the typing dots. */
export const BrainStream = ({ sessionId }: Props) => {
  const isTyping = useRtmTyping();

  return (
    <ErrorBoundary
      fallback={<TypingDots isVisible={isTyping} />}
      onError={reportStreamError}
      resetKeys={[sessionId]}
    >
      <Stream sessionId={sessionId} isTyping={isTyping} />
    </ErrorBoundary>
  );
};
